import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { filter, fromEvent } from 'rxjs';
import { ApplicationConfig } from '../config/application.config';
import { AuthActions } from '../../shared/store/auth/auth.actions';
import { AuthSelectors } from '../../shared/store/auth/auth.selectors';
import { ROUTES } from '../../shared/common/routes';
import { Account } from '../../domain/auth/model/account';
import { AuthToken } from '../../domain/auth/model/auth-token';

/**
 * Keeps every open tab on the session the browser currently holds.
 *
 * The NGXS storage plugin writes this tab's auth slice to localStorage after
 * every action. A tab that kept the previous person's session in memory would
 * therefore overwrite a sign-in made in another tab (sign out as Manager,
 * sign in as Admin, and the next reload shows the Manager again). Each tab
 * instead takes over what another tab stored, on every auth key change.
 */
@Injectable({ providedIn: 'root' })
export class AuthStorageSyncService {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly prefix = `${ApplicationConfig.applicationName}:auth.`;

  start(): void {
    fromEvent<StorageEvent>(window, 'storage')
      .pipe(
        filter(e => e.storageArea === localStorage),
        filter(e => !!e.key?.startsWith(this.prefix)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.sync());
  }

  private sync(): void {
    const tokenBefore = this.store.selectSnapshot(AuthSelectors.token);
    const accountBefore = this.store.selectSnapshot(
      AuthSelectors.getCurrentLogged
    );
    const stored = {
      token: this.read<AuthToken>('token'),
      tokenSavedAt: this.read<number>('tokenSavedAt'),
      account: this.read<Account>('account'),
      username: this.read<string>('username') ?? '',
    };

    // Only a different session counts. Another tab's sign-in form also writes
    // `username`; following that would throw this tab out of its own flow
    // (e.g. setting a first password).
    const tokenChanged =
      (tokenBefore?.accessToken ?? null) !==
      (stored.token?.accessToken ?? null);
    const personChanged =
      !!accountBefore &&
      !!stored.account &&
      (accountBefore.id !== stored.account.id ||
        accountBefore.email !== stored.account.email);
    const accountArrived = !accountBefore && !!stored.account;
    if (!tokenChanged && !personChanged && !accountArrived) {
      return;
    }

    this.store.dispatch(new AuthActions.SyncFromStorage(stored));

    if (tokenBefore && !stored.token) {
      this.router.navigate([ROUTES.AUTH.LOGIN]);
    } else if (personChanged) {
      // Someone else is signed in now: leave whatever the previous person had open.
      this.router.navigateByUrl(ROUTES.CASES);
    }
  }

  private read<T>(key: string): T | null {
    try {
      return JSON.parse(localStorage.getItem(this.prefix + key) ?? 'null');
    } catch {
      return null;
    }
  }
}
